import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { INotificationItem } from "api/notifications/notifications.type";
import { RootState } from "store/index";

// App-wide generation-completion Notifications state (REF-6306). `items` holds the currently
// loaded feed pages (page 1 replaced wholesale on load, later pages appended for paging); live WS
// frames are prepended via `prependNotification`.
interface NotificationsState {
  items: INotificationItem[];
  unreadCount: number;
  page: number;
  hasMore: boolean;
  loaded: boolean;
  // Transient top-right toast queue (REF-6306) — separate from `items` (the persistent bell feed).
  // Pushed additively by SocketListener when a WS frame's `notification.surface` is truthy; consumed
  // and dismissed by NotificationToaster. Capped at 20 stored entries to avoid unbounded growth if a
  // burst of completions arrives while the toaster isn't mounted/hovered.
  toasts: INotificationItem[];
}

const MAX_STORED_TOASTS = 20;

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  page: 1,
  hasMore: false,
  loaded: false,
  toasts: [],
};

export const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Page-1 load — replaces the list wholesale.
    setNotifications: (state, action: PayloadAction<{ items: INotificationItem[]; page: number; hasMore: boolean; unreadCount: number }>) => {
      state.items = action.payload.items;
      state.page = action.payload.page;
      state.hasMore = action.payload.hasMore;
      state.unreadCount = action.payload.unreadCount;
    },
    // Paging — appends a later page to the existing list.
    appendNotifications: (state, action: PayloadAction<{ items: INotificationItem[]; page: number; hasMore: boolean }>) => {
      state.items = [...state.items, ...action.payload.items];
      state.page = action.payload.page;
      state.hasMore = action.payload.hasMore;
    },
    // Live WS frame — insert at front; replace in place if the id already exists instead of duplicating.
    prependNotification: (state, action: PayloadAction<INotificationItem>) => {
      const existingIndex = state.items.findIndex((item) => item.id === action.payload.id);

      if (existingIndex !== -1) {
        state.items[existingIndex] = action.payload;

        return;
      }

      state.items = [action.payload, ...state.items];
    },
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
    markItemsRead: (state, action: PayloadAction<string[]>) => {
      const stamp = new Date().toISOString();
      const ids = new Set(action.payload);

      state.items.forEach((item) => {
        if (ids.has(item.id)) item.read_at = stamp;
      });
    },
    markItemsSeen: (state, action: PayloadAction<string[]>) => {
      const stamp = new Date().toISOString();
      const ids = new Set(action.payload);

      state.items.forEach((item) => {
        if (ids.has(item.id)) item.seen_at = stamp;
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    // Toast queue (REF-6306). Newest first; de-dupe by id (replace in place rather than duplicate,
    // mirroring prependNotification), capped at MAX_STORED_TOASTS.
    pushToast: (state, action: PayloadAction<INotificationItem>) => {
      const existingIndex = state.toasts.findIndex((item) => item.id === action.payload.id);

      if (existingIndex !== -1) {
        state.toasts[existingIndex] = action.payload;

        return;
      }

      state.toasts = [action.payload, ...state.toasts].slice(0, MAX_STORED_TOASTS);
    },
    dismissToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((item) => item.id !== action.payload);
    },
    clearToasts: (state) => {
      state.toasts = [];
    },
  },
});

export const { setNotifications, appendNotifications, prependNotification, setUnreadCount, markItemsRead, markItemsSeen, removeNotification, pushToast, dismissToast, clearToasts } =
  notificationsSlice.actions;

// Null-safe reads: the slice is always registered in the real store, but a partial test store (or a
// not-yet-hydrated store) may lack `notifications` — fall back to the initialState defaults rather than
// crashing the consumer (WhatsNewMenu / NotificationToaster) on `state.notifications` being undefined.
export const selectNotifications = (state: RootState) => state.notifications?.items ?? [];
export const selectNotificationUnreadCount = (state: RootState) => state.notifications?.unreadCount ?? 0;
export const selectNotificationsPage = (state: RootState) => state.notifications?.page ?? 1;
export const selectNotificationsHasMore = (state: RootState) => state.notifications?.hasMore ?? false;
export const selectNotificationsLoaded = (state: RootState) => state.notifications?.loaded ?? false;
export const selectToasts = (state: RootState) => state.notifications?.toasts ?? [];

export default notificationsSlice.reducer;
